/**
 * TanStack Query hooks for progress: the series behind the charts, the records and the canonical
 * exercise series.
 */

import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type {
  WeeklyKcal,
  WeightTrend,
  DeviceStats,
  SessionsDetailItem,
  StrengthDetailItem,
  BikeSessionItem,
  BodyProjectionResponse,
  RecordsResponse,
  SarcopeniaDeviceData,
  CanonicalExerciseOut,
  TonnagePoint,
} from '../types'
import { OPTIONAL_QUERY_DEFAULTS } from './shared'

// ── Progress ────────────────────────────────────────────────────────

/** Weekly kcal aggregates for the kcal trend chart. */
export function useProgressKcal() {
  return useQuery({
    queryKey: ['progress', 'kcal'],
    queryFn: () => apiFetch<WeeklyKcal[]>('/api/progress/kcal'),
  })
}

/** Per-session average weight trend. */
export function useProgressWeight() {
  return useQuery({
    queryKey: ['progress', 'weight'],
    queryFn: () => apiFetch<WeightTrend[]>('/api/progress/weight'),
  })
}

/** Per-device monthly stats. */
export function useProgressDevices() {
  return useQuery({
    queryKey: ['progress', 'devices'],
    queryFn: () => apiFetch<DeviceStats[]>('/api/progress/devices'),
  })
}

/** All session detail rows for progress charts. */
export function useSessionsDetail() {
  return useQuery({
    queryKey: ['progress', 'sessions-detail'],
    queryFn: () => apiFetch<SessionsDetailItem[]>('/api/progress/sessions-detail'),
  })
}

/**
 * Daily tonnage across circuit, free weights and Technogym — the Strength
 * arc's series. Replaces the Milon-only `compound_score` that made the arc
 * stop dead on the last circuit session.
 */
export function useTonnageSeries() {
  return useQuery({
    queryKey: ['progress', 'tonnage-series'],
    queryFn: () => apiFetch<TonnagePoint[]>('/api/progress/tonnage-series'),
  })
}

/** Per-device per-session strength data for strength tab charts. */
export function useStrengthDetail() {
  return useQuery({
    queryKey: ['progress', 'strength-detail'],
    queryFn: () => apiFetch<StrengthDetailItem[]>('/api/progress/strength-detail'),
  })
}

/**
 * Body composition linear regression projection (+30/+60/+90 days).
 *
 * Retained as source material for the Dashboard compartment; not dead code.
 * The Journey portrait replaced the view that rendered the 90-day forecast, so
 * this hook currently has no callers — the forecast is being rehomed to the
 * Dashboard, and this is the endpoint it will read. A routine dead-code sweep
 * should leave it alone.
 */
export function useBodyProjection() {
  return useQuery({
    queryKey: ['progress', 'body-projection'],
    queryFn: () => apiFetch<BodyProjectionResponse>('/api/progress/body-projection'),
  })
}

/** Per-session Q Bike data (watts, RPM, quality) for the Body tab bike chart. */
export function useBikeSessions() {
  return useQuery({
    queryKey: ['progress', 'bike-sessions'],
    queryFn: () => apiFetch<BikeSessionItem[]>('/api/progress/bike-sessions'),
  })
}

/** Sarcopenia reference curves + per-session history per device. */
export function useSarcopenia() {
  return useQuery({
    queryKey: ['progress', 'sarcopenia'],
    queryFn: () => apiFetch<SarcopeniaDeviceData[]>('/api/progress/sarcopenia'),
  })
}

export function useRecords() {
  return useQuery({
    queryKey: ['progress', 'records'],
    queryFn: () => apiFetch<RecordsResponse>('/api/progress/records'),
  })
}

/** Merged strength series by canonical exercise name (across Milon + Lanista variants). */
export function useCanonicalExercises() {
  return useQuery({
    queryKey: ['progress', 'exercise-canonical'],
    queryFn: () => apiFetch<CanonicalExerciseOut[]>('/api/progress/exercise-canonical'),
    ...OPTIONAL_QUERY_DEFAULTS,
  })
}
